import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { fetchJson } from "../utils";
import { CartItemType, ProductType, UserType, CartItemImageType, CartItemTypeSaved, LeadTimePriceType, CartItemWithProduct } from "../types";
import { useAppState } from "./index";
import { PRODUCT_TYPE } from "../constants";
import { useProducts } from "../pages/catalog/useProducts";

interface CartContextType {
    loading: boolean;
    cart: CartItemType[];
    cartWithProducts: CartItemWithProduct[];
    removeFromCart: (item: CartItemType, callback?: () => void) => void;
    updateCartItem: (cartItem: CartItemType, callback?: (cartItem: CartItemType) => void) => void;
    updateCartItemImage: (params: {
        cartItem: CartItemType;
        position: number;
        image: File | string;
        callback?: (updatedItem: CartItemType | undefined, updatedImage?: CartItemImageType, progress?: number) => void;
        onError?: (error: Error) => void;
        headers?: HeadersInit;
    }) => void;
    removeCartItemImage: (params: {
        cartItem: CartItemType;
        id: number;
        callback?: (updatedItem: CartItemType) => void;
        onError?: (error: Error) => void;
    }) => void;
    updateCartItemImagePositions: (
        item: Pick<CartItemTypeSaved, 'id' | 'product'>,
        positions: {id: number; position: number}[],
        callback?: (updatedItem: CartItemType) => void
    ) => void;
    isInCart: (product: ProductType) => boolean;
    findCartItem: (product: ProductType) => CartItemType | undefined;
    itemCount: number;
    totalCost: number | undefined;
    baseCost: number | undefined;
    couponValue: string;
    setCouponValue: (value: string) => void;
    couponError: string;
    handleCouponApply: () => Promise<void>;
    checkoutReadyData: [boolean, string[]];
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user] = useAppState<UserType>('user', undefined);
    const [cart, setCart] = useAppState<CartItemType[]>('cart', []);
    const cartLoaded = useRef<boolean>(false);
    const loading = useRef<boolean>(false);
    const [checkoutReadyData, setCheckoutReadyData] = useAppState<[boolean, string[]]>('checkoutReadyData', [false, []]);
    const [couponValue, setCouponValue] = useState<string>('');
    const [couponError, setCouponError] = useState<string>('');
    const [totalCost, setTotalCost] = useState<number | undefined>(undefined);
    const [baseCost, setBaseCost] = useState<number | undefined>(undefined);
    const [cartWithProducts, setCartWithProducts] = useState<CartItemWithProduct[]>([]);
    const { findProducts } = useProducts();
    const userOnload = useRef(user);

    useEffect(() => {
        if (!user) {
            cartLoaded.current = false;
            loading.current = false;
            return;
        }
        if (cartLoaded.current || loading.current) {
            return;
        }

        // load cart only once per user when user is signed in
        if (userOnload.current && user?.id === userOnload.current?.id) {
            return;
        }

        loading.current = true;
        fetchJson<CartItemType[]>('/api/cart')
            .then(({json}) => {
                setCart(json);
            })
            .finally(() => {
                loading.current = false;
                cartLoaded.current = true;
            })
            .catch(error => {
                console.error(error);
            });
    }, [user]);

    useEffect(() => {
        if (!cart.length) {
            if (findProducts.length > 0) {
                setCartWithProducts([]);
            }
            return;
        }

        const loadProducts = async () => {
            try {
                const products = await findProducts(cart.map(item => Number(item.product)));
                setCartWithProducts(cart.map(v => ({
                    cartItem: v,
                    product: products.find(p => p.id === v.product) as ProductType
                })));
            } catch (error) {
                console.error('Failed to load products', error);
            }
        };

        loadProducts();
    }, [cart]);

    useEffect(() => {
        setTotalCost(cartWithProducts.reduce((acc, {cartItem, product}) => {
            if (!product.isAvailable) return acc;
            return acc + cartItem.baseCost + 
                ((cartItem.discountAmount || 0) * -1);
        }, 0));
    }, [cartWithProducts]);

    useEffect(() => {
        setBaseCost(cartWithProducts.reduce((acc, {cartItem, product}) => {
            if (!product.isAvailable) return acc;
            return acc + cartItem.baseCost;
        }, 0));
    }, [cartWithProducts]);

    useEffect(() => {
        if (!baseCost) {
            return;
        }
        setCheckoutReadyData(cartWithProducts.reduce(
            (current: [boolean, string[]], item) => {
                const [acc, reasons] = current;
                if (!acc) {
                    return current;
                }
                if (!item.product.isAvailable) {
                    return [false, reasons];
                }
                if (item.product.type === PRODUCT_TYPE.SIMPLE) {
                    return [true, reasons];
                }
                if (!item.cartItem.leadTimePrice) {
                    return [false, [...reasons, `select a lead time price for ${item.product.name}`]];
                }
                if (item.cartItem.captions?.length !== item.product.captionFields?.length) {
                    const missingFieldLabel = item.product.captionFields?.find(field => !item.cartItem.captions?.find(caption => caption.fieldId === field.id));
                    return [false, [...reasons, `add ${missingFieldLabel?.label?.toLowerCase() || ''} for ${item.product.name}`]];
                }

                if (!item.cartItem.images?.length) {
                    return [false, [...reasons, `upload photos for ${item.product.name}`]];
                }
                return [true, reasons];
            },
            [baseCost > 0, []]
        ));
    }, [cartWithProducts, totalCost]);

    const handleCouponApply = useCallback(async () => {
        try {
            const { json } = await fetchJson<{
                success: boolean;
                message?: string;
                cart?: CartItemType[];
            }>('/api/cart/apply-coupon', {
                method: 'POST',
                body: JSON.stringify({ coupon: couponValue })
            });

            if (json.success) {
                setCouponError('');
                if (json.cart) {
                    setCart(json.cart);
                }
            } else {
                setCouponError(json.message || '');
            }
        } catch (error) {
            setCouponError('Failed to apply coupon');
        }
    }, [couponValue]);

    const updateCartItem = useCallback((cartItem: CartItemType, callback?: (cartItem: CartItemType) => void) => {
        fetchJson<CartItemType[]>('/api/cart/update', {
            method: 'POST',
            body: JSON.stringify(cartItem),
        })
            .then(({json}) => {
                setCart(json);
                if (callback) {
                    callback(json.find(v => v.product === cartItem.product) as CartItemType);
                }
            })
            .finally(() => {
                loading.current = false;
            })
            .catch(error => {
                console.error(error);
            });
    }, []);

    const updateCartItemImage = useCallback(({
        cartItem,
        image,
        callback,
        position,
        onError,
        headers,
    }: {
        cartItem: CartItemType;
        position: number;
        image: File | string;
        callback?: (updatedItem: CartItemType | undefined, updatedImage?: CartItemImageType, progress?: number) => void;
        onError?: (error: Error) => void;
        headers?: HeadersInit;
    }) => {
        const formData = new FormData();
        formData.append("image", image);
        formData.append("cartItem", String(cartItem.id));
        formData.append("position", String(position));

        loading.current = true;

        const xhr = new XMLHttpRequest();
        
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable && callback) {
                const progress = (event.loaded / event.total) * 100;
                callback(undefined, undefined, progress);
            }
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const json = JSON.parse(xhr.responseText) as CartItemType[];
                    setCart(json);
                    if (callback) {
                        const updatedItem = json.find((v) => v.product === cartItem.product);
                        if (updatedItem) {
                            const imageId = xhr.getResponseHeader('x-cart-item-image-id');
                            callback(
                                updatedItem, 
                                updatedItem.images.find(v => v.id === Number(imageId)),
                                100
                            );
                        } else {
                            console.warn("Updated cart item not found");
                        }
                    }
                } catch (error) {
                    const err = new Error('Invalid JSON response');
                    console.error("Failed to update cart item image:", err);
                    if (onError) onError(err);
                }
            } else {
                const err = new Error(`HTTP error! status: ${xhr.status}`);
                console.error("Failed to update cart item image:", err);
                if (onError) onError(err);
            }
            loading.current = false;
        };

        xhr.onerror = () => {
            const err = new Error('Network request failed');
            console.error("Failed to update cart item image:", err);
            if (onError) onError(err);
            loading.current = false;
        };

        xhr.open('POST', "/api/cart/image", true);
        
        
        xhr.setRequestHeader('Accept', 'application/json');
        
    
        // Apply custom headers if provided (will override defaults if same header name)
        if (headers) {
            if (headers instanceof Headers) {
                headers.forEach((value, key) => {
                    xhr.setRequestHeader(key, value);
                });
            } else {
                Object.entries(headers).forEach(([key, value]) => {
                    if (typeof value === 'string') {
                        xhr.setRequestHeader(key, value);
                    }
                });
            }
        }

        xhr.send(formData);
    }, []);

    const removeCartItemImage = useCallback(({
        cartItem,
        id,
        callback,
        onError,
    }: {
        cartItem: CartItemType;
        id: number;
        callback?: (updatedItem: CartItemType) => void;
        onError?: (error: Error) => void;
    }) => {
        loading.current = true;

        fetchJson<CartItemType[]>("/api/cart/image", {
            method: "DELETE",
            body: JSON.stringify({
                id,
                cartItem: cartItem.id,
            }),
        })
            .then(({json}) => {
                setCart(json);
                if (callback) {
                    const updatedItem = json.find((v) => v.product === cartItem.product);
                    if (updatedItem) {
                        callback(updatedItem);
                    } else {
                        console.warn("Updated cart item not found");
                    }
                }
            })
            .catch((error) => {
                console.error("Failed to remove cart item image:", error);
                if (onError) {
                    onError(error);
                }
            })
            .finally(() => {
                loading.current = false;
            });
    }, []);

    const updateCartItemImagePositions = useCallback((
        {id, product}: Pick<CartItemTypeSaved, 'id' | 'product'>,
        positions: {id: number; position: number}[],
        callback?: (updatedItem: CartItemType) => void
    ) => {
        loading.current = true;
        fetchJson<CartItemType[]>('/api/cart/update-positions', {
            method: 'POST',
            body: JSON.stringify({
                cartItem: id,
                positions
            })
        })
            .then(({json}) => {
                setCart(json);
                if (callback) {
                    const updatedItem = json.find((v) => v.product === product);
                    if (updatedItem) {
                        callback(updatedItem);
                    }
                }
            })
            .finally(() => {
                loading.current = false;
            })
            .catch(error => {
                console.error(error);
            });
    }, []);

    const removeFromCart = useCallback(({id}: CartItemType, callback?: () => void) => {
        fetchJson<CartItemType[]>('/api/cart/remove', {
            method: 'POST',
            body: JSON.stringify({
                id
            })
        })
            .then(({json}) => {
                setCart(json);
                if (callback) {
                    callback();
                }
            })
            .finally(() => {
                loading.current = false;
            })
            .catch(error => {
                console.error(error);
            });
    }, []);

    const isInCart = useCallback((product: ProductType) => {
        return findCartItem(product) !== undefined;
    }, [cart]);

    const findCartItem = useCallback((product: ProductType) => {
        return cart.find(v => v.product === product.id);
    }, [cart]);

    const itemCount = useMemo(() => cartWithProducts.length, [cartWithProducts]);

    const value = useMemo(() => ({
        loading: loading.current,
        cart,
        cartWithProducts,
        removeFromCart,
        updateCartItem,
        updateCartItemImage,
        removeCartItemImage,
        updateCartItemImagePositions,
        isInCart,
        findCartItem,
        itemCount,
        totalCost,
        baseCost,
        couponValue,
        setCouponValue,
        couponError,
        handleCouponApply,
        checkoutReadyData: [checkoutReadyData[0], checkoutReadyData[1]] as [boolean, string[]]
    }), [
        loading.current,
        cart,
        cartWithProducts,
        baseCost,
        totalCost,
        couponValue,
        couponError,
        checkoutReadyData,
        removeFromCart,
        updateCartItem,
        updateCartItemImage,
        removeCartItemImage,
        updateCartItemImagePositions,
        isInCart,
        findCartItem
    ]);

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = (): CartContextType => {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
}; 